import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Wrench, Truck, ShieldCheck, Headphones } from 'lucide-react';
import { apiGet } from '../lib/api';
import ProductCard from '../components/ProductCard';
import Marquee from '../components/Marquee';
import Reveal from '../components/Reveal';

const HERO_IMG =
  'https://images.unsplash.com/photo-1706862609885-7771b001daa2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const line = {
  hidden: { y: '110%' },
  show: (i) => ({ y: '0%', transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const VALUES = [
  { icon: Truck, title: 'Fast US Shipping', text: 'Free shipping on orders over $99, nationwide.' },
  { icon: ShieldCheck, title: '1-Year Warranty', text: 'Every part backed by our full replacement warranty.' },
  { icon: Wrench, title: 'OEM-Fit Guaranteed', text: 'Machined and tested to factory specification.' },
  { icon: Headphones, title: '24/7 Tech Support', text: 'Real parts specialists on the line, day or night.' },
];

function HeroSelector({ brands, categories }) {
  const nav = useNavigate();
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [cat, setCat] = useState('');
  const sel = brands.find((b) => b.name === brand);
  const go = () => {
    const p = new URLSearchParams();
    if (brand) p.set('brand', brand);
    if (cat) p.set('category', cat);
    nav(`/shop?${p.toString()}`);
  };
  const cls =
    'bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-sm text-cream focus:outline-none focus:border-flame/70 min-w-0';
  return (
    <div data-testid="hero-fitment-selector" className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-2.5 bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-3">
      <select data-testid="find-parts-brand-select" value={brand} onChange={(e) => { setBrand(e.target.value); setModel(''); }} className={cls}>
        <option value="" className="text-ink">Tractor Brand</option>
        {brands.map((b) => <option key={b.id} value={b.name} className="text-ink">{b.name}</option>)}
      </select>
      <select data-testid="find-parts-model-select" value={model} onChange={(e) => setModel(e.target.value)} disabled={!sel} className={`${cls} disabled:opacity-40`}>
        <option value="" className="text-ink">{sel ? 'Model' : 'Pick brand first'}</option>
        {sel?.models.map((m) => <option key={m} value={m} className="text-ink">{m}</option>)}
      </select>
      <select data-testid="find-parts-category-select" value={cat} onChange={(e) => setCat(e.target.value)} className={cls}>
        <option value="" className="text-ink">All Part Categories</option>
        {categories.map((c) => <option key={c.id} value={c.slug} className="text-ink">{c.name}</option>)}
      </select>
      <button data-testid="hero-fitment-go-button" onClick={go} className="bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide rounded-full px-4 py-2.5 transition-colors">
        Show Matching Parts
      </button>
    </div>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [spot, setSpot] = useState({ x: 60, y: 40 });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);

  useEffect(() => {
    apiGet('/products', { featured: 'true', limit: 8 }).then((d) => setProducts(d.items)).catch(() => {});
    apiGet('/brands').then(setBrands).catch(() => {});
    apiGet('/categories').then(setCategories).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setSpot({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
        }}
        className="relative bg-forest noise overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none transition-[background] duration-200"
          style={{ background: `radial-gradient(600px circle at ${spot.x}% ${spot.y}%, rgba(255,87,34,0.12), transparent 65%)` }}
        />
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full border border-white/5 spin-slow pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-5"
            >
              ▚ Serving America's farms, shops & dealers
            </motion.p>
            <h1 className="font-display font-extrabold uppercase text-cream tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              {['Keep Your', 'Tractor', 'Running Strong.'].map((t, i) => (
                <span key={i} className="hero-line-mask">
                  <motion.span
                    custom={i}
                    variants={line}
                    initial="hidden"
                    animate="show"
                    className={`block ${i === 1 ? 'text-flame' : ''}`}
                  >
                    {t}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="text-cream/70 text-base sm:text-lg leading-relaxed mt-6 max-w-md"
            >
              Shop dependable tractor parts and agricultural equipment components at competitive prices —
              shipped fast, anywhere in the United States.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="flex flex-wrap gap-3 mt-8"
            >
              <Link
                to="/shop"
                data-testid="hero-shop-parts-button"
                className="inline-flex items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors"
              >
                Shop All Parts <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/find-parts"
                data-testid="hero-find-parts-button"
                className="inline-flex items-center gap-2 border-2 border-cream/25 hover:border-flame text-cream hover:text-flame font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors"
              >
                <Wrench className="w-4 h-4" /> Find Parts for My Tractor
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}>
              <HeroSelector brands={brands} categories={categories} />
            </motion.div>
          </div>

          <motion.div
            initial={{ clipPath: 'inset(8% 8% 8% 8% round 24px)', opacity: 0 }}
            animate={{ clipPath: 'inset(0% 0% 0% 0% round 24px)', opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
              <motion.img
                src={HERO_IMG}
                alt="Tractor working a field at dusk"
                style={{ y: imgY }}
                className="w-full h-[340px] sm:h-[420px] lg:h-[520px] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest2 text-flame">In the field since day one</p>
                  <p className="font-display font-bold uppercase text-cream text-2xl sm:text-3xl leading-tight">
                    Parts that earn their keep
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 bg-flame text-white rounded-2xl px-5 py-4 shadow-xl hidden sm:block">
              <p className="font-display font-bold uppercase text-3xl leading-none">10</p>
              <p className="font-mono text-[9px] uppercase tracking-widest mt-1">Part categories<br />in stock</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="flex gap-4 items-start">
              <v.icon className="w-6 h-6 text-flame shrink-0 mt-1" />
              <div>
                <h3 className="font-display font-semibold uppercase text-cream">{v.title}</h3>
                <p className="text-sm text-cream/50 mt-1">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">01 — Shop by category</p>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl sm:text-5xl leading-none">
                Every system,<br />every fix.
              </h2>
            </div>
            <Link to="/shop" data-testid="browse-all-categories-link" className="font-display font-semibold uppercase tracking-wide text-forest border-b-2 border-flame pb-1 hover:text-flame transition">
              Browse all parts →
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: (i % 5) * 0.06 }}
              >
                <Link
                  to={`/shop?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group relative block rounded-2xl overflow-hidden bg-forest aspect-[4/5]"
                >
                  <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <p className="font-mono text-[9px] uppercase tracking-widest text-flame mb-1">{c.product_count} parts</p>
                    <h3 className="font-display font-bold uppercase text-cream leading-tight text-base sm:text-lg">{c.name}</h3>
                    <p className="text-[11px] text-cream/60 mt-1 opacity-0 group-hover:opacity-100 max-h-0 group-hover:max-h-12 transition-all duration-300 leading-snug">{c.blurb}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED */}
      <section className="bg-cream py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">02 — Featured parts</p>
              <h2 className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl sm:text-5xl leading-none">
                What the shops<br />keep re-ordering.
              </h2>
            </div>
            <Link to="/shop" data-testid="shop-all-featured-link" className="font-display font-semibold uppercase tracking-wide text-forest border-b-2 border-flame pb-1 hover:text-flame transition">
              Shop all parts →
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {products.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="bg-forest noise relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">03 — Shop by tractor brand</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
              Fitment for the big six.
            </h2>
            <p className="text-cream/50 text-sm mt-3 max-w-lg">
              We are an independent supplier and are not affiliated with or authorized by these manufacturers.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {brands.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
              >
                <Link
                  to={`/shop?brand=${encodeURIComponent(b.name)}`}
                  data-testid={`brand-card-${b.slug}`}
                  className="group block bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-flame/50 rounded-2xl p-6 transition-all duration-300 h-full"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold uppercase text-3xl sm:text-4xl text-cream group-hover:text-flame transition-colors tracking-tight">
                      {b.name}
                    </span>
                    <ArrowRight className="w-5 h-5 text-cream/30 group-hover:text-flame group-hover:translate-x-1 transition-all" />
                  </div>
                  <p className="text-sm text-cream/50 mt-2">{b.tagline}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {b.models.slice(0, 4).map((m) => (
                      <span key={m} className="font-mono text-[10px] text-cream/60 border border-white/10 rounded-full px-2.5 py-1">{m}</span>
                    ))}
                    <span className="font-mono text-[10px] text-flame px-1.5 py-1">{b.product_count} parts</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">Not sure what fits?</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
              Pick your tractor.<br />We'll find the parts.
            </h2>
            <p className="text-cream/60 mt-5 max-w-md">
              Three quick steps — brand, model, category — and we'll show every part that matches your machine.
            </p>
            <Link
              to="/find-parts"
              data-testid="cta-find-parts-button"
              className="inline-flex items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide px-7 py-3.5 rounded-full transition-colors mt-8"
            >
              <Wrench className="w-4 h-4" /> Start the Parts Finder
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1568680870491-590cd4e224ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Tractor in a field at dusk"
                loading="lazy"
                className="w-full h-64 sm:h-80 object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
